import { useEffect, useRef, useState, useCallback } from 'react';
import { IPostAutoComplete } from '@/redux/post/post';
import { useLazyGetSearchAutoCompleteQuery } from '@/redux/post/post.api.slice';
import { debounce } from '@/lib/utils';
import useHandleClickOutside from '@/customHooks/useHandleClickOutside';

function useGlobalSearchSuggestions(query: string) {
  const [suggestions, setSuggestions] = useState<IPostAutoComplete[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [getSearchAutoComplete] = useLazyGetSearchAutoCompleteQuery();

  const queryRef = useRef(query);
  const abortRef = useRef<() => void>();

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const suggestionsRef = useHandleClickOutside<HTMLDivElement>(
    () => {
      setShowSuggestions(false);
    },
    {
      enabled: showSuggestions,
      excludeRefs: [inputRef],
    }
  );

  // Create the debounced function outside of the effect
  // so it doesn't get recreated on each query change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchSuggestions = useCallback(
    debounce(async () => {
      const currentQuery = queryRef.current;

      if (currentQuery.length <= 2 || currentQuery.trim() === '') {
        setSuggestions([]);
        return;
      }

      // Abort previous request if exists
      if (abortRef.current) {
        abortRef.current();
      }

      const { unwrap, abort } = getSearchAutoComplete({
        searchQuery: currentQuery,
      });

      abortRef.current = abort;

      try {
        const searchSuggestions = await unwrap();
        setSuggestions(searchSuggestions);

        if (searchSuggestions.length > 0) {
          setShowSuggestions(true);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          setSuggestions([]);
        }
      }
    }, 500),
    []
  );

  // Trigger the debounced function when query changes
  useEffect(() => {
    // For empty or short queries, clear suggestions immediately without debouncing
    if (query.length <= 3 || query.trim() === '') {
      setSuggestions([]);
      return;
    }

    // Call the debounced function
    debouncedFetchSuggestions();

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      // Abort any ongoing API request
      if (abortRef.current) {
        abortRef.current();
      }
    };
  }, [query, debouncedFetchSuggestions]);

  return {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    suggestionsRef,
    inputRef,
  };
}

export default useGlobalSearchSuggestions;
